import { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Loader2, Globe, Users, QrCode, Trash2, PowerOff, Save, ShieldCheck, RefreshCw, Download, UsersRound, Lock, ChevronRight, AlertTriangle,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useInstances, type Instance } from "@/hooks/useInstances";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FlagImage, defaultCountries, guessCountryByPartialPhoneNumber } from "react-international-phone";
import type { Tables } from "@/integrations/supabase/types";
import { WhatsappProfileCard } from "@/components/WhatsappProfileCard";

const getStageEmoji = (s: string | null) => { switch(s) { case "Cold": return "❄️"; case "Warm": return "🟡"; case "Hot": return "🔥"; case "Mature": return "🛡️"; default: return "❄️"; } };
const getStageLabel = (s: string | null) => { switch(s) { case "Cold": return "Frio"; case "Warm": return "Morno"; case "Hot": return "Quente"; case "Mature": return "Maduro"; default: return "Frio"; } };
const getHeatColor = (s: number) => s >= 85 ? "from-red-500 to-orange-400" : s >= 60 ? "from-orange-400 to-yellow-400" : s >= 35 ? "from-yellow-400 to-blue-300" : "from-blue-400 to-blue-200";

function formatPhone(phone: string) {
  const clean = phone.replace(/\D/g, "");
  if (clean.length === 13) return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 9)}-${clean.slice(9)}`;
  if (clean.length === 12) return `+${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 8)}-${clean.slice(8)}`;
  return phone;
}

function WarmupTimeline({ profiles, activeProfileId }: { profiles: Tables<"warmup_profiles">[]; activeProfileId: string }) {
  const systemProfiles = profiles.filter((p) => p.is_system_preset).sort((a, b) => (a.chain_order ?? 0) - (b.chain_order ?? 0));
  const activeProfile = profiles.find((p) => p.id === activeProfileId);
  const isCustomActive = activeProfile && !activeProfile.is_system_preset;

  return (
    <div className="space-y-1">
      {systemProfiles.map((p, i) => {
        const isLast = i === systemProfiles.length - 1 && !isCustomActive;
        const isCurrent = p.id === activeProfileId;
        const phaseNum = (p.chain_order ?? i) + 1;
        return (
          <div key={p.id} className="relative flex gap-3">
            {/* Timeline spine */}
            <div className="flex flex-col items-center shrink-0 w-8">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10 ${
                isCurrent ? "bg-primary border-2 border-primary text-primary-foreground" : "bg-primary/10 border-2 border-primary/30 text-primary/50"
              }`}>
                {phaseNum}
              </div>
              {(!isLast || isCustomActive) && (
                <div className={`flex-1 w-0.5 min-h-[8px] ${isCurrent ? "bg-primary/40" : "bg-primary/15"}`} />
              )}
            </div>
            {/* Content */}
            <div className={`flex-1 pb-2 rounded-xl p-3 mb-1 transition-all ${
              isCurrent ? "bg-primary/10 border border-primary/30 ring-1 ring-primary/20" : "opacity-50"
            }`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{p.ui_icon || "⚙️"}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className={`text-sm font-semibold ${isCurrent ? "text-primary" : "text-foreground"}`}>{p.name}</p>
                    <Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 gap-0.5 px-1 py-0">
                      <Lock className="h-2 w-2" /> Sistema
                    </Badge>
                    {isCurrent && <Badge className="text-[9px] bg-primary/20 text-primary border-0 px-1.5 py-0">Atual</Badge>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {p.min_daily_msgs}–{p.max_daily_msgs} msgs/dia · {(p.period_start || "08:00").slice(0, 5)}–{(p.period_end || "20:00").slice(0, 5)}
                    {p.duration_days ? ` · ${p.duration_days}d` : ""}
                  </p>
                </div>
                {!isLast && !isCurrent && <ChevronRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />}
              </div>
              {isCurrent && (
                <div className="flex gap-1.5 flex-wrap mt-2">
                  <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">📝 {p.prob_text}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Texto</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">🎙️ {p.prob_audio}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Áudio</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">🖼️ {p.prob_image}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Imagem</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">😄 {p.prob_sticker}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Figurinha</TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild><Badge variant="outline" className={`text-[9px] cursor-help ${(p as any).status_enabled ? "border-primary/40 text-primary" : "border-warmz-glass-border/50"}`}>📢 {(p as any).status_enabled ? `${(p as any).status_per_day}/dia` : "Off"}</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Stories</TooltipContent></Tooltip>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* If active profile is a custom one, show it at the end */}
      {isCustomActive && activeProfile && (
        <div className="relative flex gap-3">
          <div className="flex flex-col items-center shrink-0 w-8">
            <div className="w-7 h-7 rounded-full bg-primary border-2 border-primary text-primary-foreground flex items-center justify-center text-xs font-bold z-10">
              ⚙️
            </div>
          </div>
          <div className="flex-1 pb-2 rounded-xl p-3 bg-primary/10 border border-primary/30 ring-1 ring-primary/20">
            <div className="flex items-center gap-2">
              <span className="text-lg">{activeProfile.ui_icon || "⚙️"}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-sm font-semibold text-primary">{activeProfile.name}</p>
                  <Badge className="text-[9px] bg-primary/20 text-primary border-0 px-1.5 py-0">Atual</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {activeProfile.min_daily_msgs}–{activeProfile.max_daily_msgs} msgs/dia · {(activeProfile.period_start || "08:00").slice(0, 5)}–{(activeProfile.period_end || "20:00").slice(0, 5)}
                </p>
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap mt-2">
              <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">📝 {activeProfile.prob_text}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Texto</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">🎙️ {activeProfile.prob_audio}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Áudio</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">🖼️ {activeProfile.prob_image}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Imagem</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[9px] border-warmz-glass-border/50 cursor-help">😄 {activeProfile.prob_sticker}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Figurinha</TooltipContent></Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QrCodeDisplay({ qrCode, isPolling }: { qrCode: string | null; isPolling: boolean }) {
  if (!qrCode) {
    return (
      <div className="w-48 h-48 rounded-2xl bg-secondary/40 flex items-center justify-center">
        {isPolling ? <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" /> : <QrCode className="h-16 w-16 text-muted-foreground/50" />}
      </div>
    );
  }
  return (
    <div className="relative">
      <img src={qrCode.startsWith("data:") ? qrCode : `data:image/png;base64,${qrCode}`} alt="QR Code" className="w-48 h-48 rounded-2xl" />
      <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 animate-pulse" />
    </div>
  );
}

function ToggleRow({ icon: Icon, iconClass, title, description, checked, onCheckedChange, disabled }: {
  icon: any; iconClass?: string; title: string; description: string;
  checked: boolean; onCheckedChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg bg-secondary/30 ${disabled ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-3">
        <Icon className={`h-4 w-4 shrink-0 ${iconClass || "text-accent"}`} />
        <div>
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground max-w-[280px]">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

export default function NumberEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { instances, warmupProfiles, updateInstance, disconnectInstance, pollQrCode, syncContacts, manageGroupMembership } = useInstances();

  const instance = instances.find((i) => i.id === id);

  const [profileId, setProfileId] = useState("");
  const [nickname, setNickname] = useState("");
  const [accountType, setAccountType] = useState("Personal");
  const [officialContacts, setOfficialContacts] = useState(false);
  const [warmupGroups, setWarmupGroups] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // (system groups are managed automatically by engine-tick)

  // QR reconnect state
  const [showQr, setShowQr] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: contactTags = [] } = useQuery({
    queryKey: ["contact_tags"],
    queryFn: async () => {
      const { data, error } = await supabase.from("contact_tags").select("*").order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: customContacts = [] } = useQuery({
    queryKey: ["custom_contacts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("custom_contacts").select("*").eq("is_active", true).order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: userGroups = [] } = useQuery({
    queryKey: ["user_warmup_groups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warmup_groups")
        .select("*")
        .eq("is_system", false)
        .eq("user_id", user!.id)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (instance && !loaded) {
      setProfileId(instance.profile_id);
      setNickname(instance.nickname || "");
      setAccountType(instance.account_type || "Personal");
      setOfficialContacts(instance.is_official_contacts_active || false);
      const groupsActive = (instance as any).is_warmup_groups_active || false;
      setWarmupGroups(groupsActive);
      setLoaded(true);

      // Load contact targets
      supabase.from("instance_contact_targets").select("custom_contact_id, contact_tag_id").eq("instance_id", instance.id).then(({ data }) => {
        if (data) {
          setSelectedTags(data.filter((t) => t.contact_tag_id).map((t) => t.contact_tag_id!));
          setSelectedContacts(data.filter((t) => t.custom_contact_id).map((t) => t.custom_contact_id!));
        }
      });

      // Load selected user groups
      supabase.from("instance_group_memberships").select("warmup_group_id").eq("instance_id", instance.id).in("status", ["joined", "pending"]).then(({ data }) => {
        if (data) {
          setSelectedGroups(data.map((m) => m.warmup_group_id));
        }
      });
    }
  }, [instance, loaded]);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback((instId: string) => {
    setIsPolling(true);
    pollingRef.current = setInterval(async () => {
      try {
        const result = await pollQrCode(instId);
        if (result.state === "open") {
          stopPolling();
          setShowQr(false);
          queryClient.invalidateQueries({ queryKey: ["instances"] });
        } else if (result.qr_code) {
          setQrCode(result.qr_code);
        }
      } catch { /* ignore */ }
    }, 5000);
  }, [pollQrCode, stopPolling, queryClient]);

  const handleReconnect = async () => {
    if (!instance) return;
    setShowQr(true);
    setQrCode(null);
    setIsPolling(true);
    try {
      const { data, error } = await supabase.functions.invoke("disconnect-instance", {
        body: { instance_id: instance.id, action: "restart" },
      });
      if (error) throw error;
      if (data?.qr_code) setQrCode(data.qr_code);
      startPolling(instance.id);
    } catch (e) {
      console.error("Reconnect error:", e);
      setIsPolling(false);
    }
  };

  const handleSave = async () => {
    if (!instance) return;
    setSaving(true);
    try {
      await updateInstance.mutateAsync({
        id: instance.id,
        profile_id: profileId,
        account_type: accountType,
        nickname: nickname || null,
        is_official_contacts_active: officialContacts,
        is_warmup_groups_active: warmupGroups,
      } as any);

      // Save contact targets
      await supabase.from("instance_contact_targets").delete().eq("instance_id", instance.id);
      const inserts: any[] = [];
      for (const tagId of selectedTags) inserts.push({ instance_id: instance.id, contact_tag_id: tagId });
      for (const cId of selectedContacts) inserts.push({ instance_id: instance.id, custom_contact_id: cId });
      if (inserts.length > 0) await supabase.from("instance_contact_targets").insert(inserts);

      // Save user group memberships (only for user's custom groups, not system)
      const { data: existingMemberships } = await supabase
        .from("instance_group_memberships")
        .select("id, warmup_group_id")
        .eq("instance_id", instance.id);

      const existingGroupIds = new Set((existingMemberships || []).map(m => m.warmup_group_id));
      const userGroupIds = new Set(userGroups.map(g => g.id));

      // Delete memberships for user groups that are no longer selected
      const toDelete = (existingMemberships || []).filter(m => userGroupIds.has(m.warmup_group_id) && !selectedGroups.includes(m.warmup_group_id));
      for (const m of toDelete) {
        await supabase.from("instance_group_memberships").update({ status: "left", left_at: new Date().toISOString() }).eq("id", m.id);
      }

      // Insert new memberships for newly selected user groups
      const toInsert = selectedGroups.filter(gId => userGroupIds.has(gId) && !existingGroupIds.has(gId));
      if (toInsert.length > 0) {
        await supabase.from("instance_group_memberships").insert(
          toInsert.map(gId => ({ instance_id: instance.id, warmup_group_id: gId, status: "pending" }))
        );
      }

      // System groups join/leave is handled automatically by engine-tick based on is_warmup_groups_active flag

      // Validate contacts_saved via edge function
      if (instance.evolution_connection_state === "open") {
        toast.info("Verificando contatos salvos...");
        try {
          const { data: checkResult, error: checkErr } = await supabase.functions.invoke("check-contacts-saved", {
            body: { instance_id: instance.id },
          });
          if (!checkErr && checkResult) {
            if (checkResult.contacts_saved) {
              toast.success("✅ Contato salvo por todos os chips! Aquecimento ativo.");
            } else if (checkResult.reason === "no_other_online_instances") {
              toast.warning("Nenhum outro chip online para validar. Conecte mais números.");
            } else {
              const missing = (checkResult.details || []).filter((d: any) => !d.saved).map((d: any) => d.instance_phone);
              toast.error(`⚠️ Contato NÃO salvo por: ${missing.join(", ")}. Salve na agenda do WhatsApp desses chips.`);
            }
          }
        } catch (e) {
          console.error("check-contacts-saved error:", e);
        }
      }

      // Refresh instance data so UI updates
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      toast.success("Alterações salvas!");
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Erro ao salvar alterações.");
    } finally {
      setSaving(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!instance) return;
    setCheckingStatus(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-instance-status", {
        body: { instance_id: instance.id },
      });
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      const stateLabel = data?.state === "open" ? "Online" : "Offline";
      toast.success(`Status atualizado: ${stateLabel}`);
    } catch (e: any) {
      console.error("Check status error:", e);
      toast.error("Erro ao consultar status.");
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleDisconnect = async () => {
    if (!instance) return;
    await disconnectInstance.mutateAsync({ instance_id: instance.id, action: "disconnect" });
    queryClient.invalidateQueries({ queryKey: ["instances"] });
  };

  const handleDelete = async () => {
    if (!instance) return;
    await disconnectInstance.mutateAsync({ instance_id: instance.id, action: "delete" });
    navigate("/dashboard/numbers");
  };

  if (!instance) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/numbers")}><ArrowLeft className="h-5 w-5" /></Button>
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const isOnline = instance.evolution_connection_state === "open";
  const score = instance.health_score || 0;
  const flagIso = guessCountryByPartialPhoneNumber({ phone: "+" + instance.phone_number.replace(/\D/g, ""), countries: defaultCountries }).country?.iso2 || "br";
  const hasContacts = contactTags.length > 0 || customContacts.length > 0;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { stopPolling(); navigate("/dashboard/numbers"); }}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <FlagImage iso2={flagIso} size="22px" />
              <h1 className="font-display text-2xl font-bold text-foreground">{formatPhone(instance.phone_number)}</h1>
            </div>
            <p className="text-muted-foreground text-sm mt-0.5">
              {nickname || "Sem apelido"} · {isOnline ? "🟢 Online" : "🔴 Offline"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleCheckStatus} disabled={checkingStatus} title="Atualizar status">
            <RefreshCw className={`h-3.5 w-3.5 ${checkingStatus ? "animate-spin" : ""}`} />
          </Button>
          {isOnline ? (
            <Button variant="outline" size="sm" className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={handleDisconnect} disabled={disconnectInstance.isPending}>
              <PowerOff className="h-3.5 w-3.5" /> Desconectar
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="gap-1.5 text-accent border-accent/30 hover:bg-accent/10" onClick={handleReconnect}>
              <QrCode className="h-3.5 w-3.5" /> Reconectar
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5 hover:text-destructive hover:border-destructive/30 hover:bg-destructive/10" onClick={() => setShowDeleteConfirm(true)} disabled={disconnectInstance.isPending}>
            <Trash2 className="h-3.5 w-3.5" /> Remover
          </Button>
        </div>
      </div>

      {/* Reconnect QR */}
      {showQr && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="flex flex-col items-center gap-4 p-8">
              <QrCodeDisplay qrCode={qrCode} isPolling={isPolling} />
              <p className="text-xs text-muted-foreground">WhatsApp → Aparelhos Conectados → Conectar</p>
              {isPolling && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Aguardando conexão...
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => { stopPolling(); setShowQr(false); }}>Cancelar</Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* WhatsApp Profile (live from Evolution API) */}
      {isOnline && <WhatsappProfileCard instanceId={instance.id} fallbackPhone={instance.phone_number} />}

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>

        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Dias Online</p>
                <p className="font-display text-xl font-bold text-foreground">{instance.consecutive_online_days || 0}d</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Health Score</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${getHeatColor(score)}`} style={{ width: `${score}%` }} />
                  </div>
                  <span className="text-sm font-bold text-foreground">{score}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Estágio</p>
                <p className="font-medium text-foreground mt-1">{getStageEmoji(instance.warmup_stage)} {getStageLabel(instance.warmup_stage)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <p className={`font-medium mt-1 ${isOnline ? "text-accent" : "text-destructive"}`}>{isOnline ? "Online" : "Offline"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Main grid: 2 columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left Column: General Settings + Toggles */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-6">
          {/* General */}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-5 space-y-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Configurações Gerais</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Apelido / Nome</Label>
                  <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Ex: Chip Principal" className="bg-secondary/40 border-warmz-glass-border h-9 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tipo de Conta</Label>
                  <Select value={accountType} onValueChange={setAccountType}>
                    <SelectTrigger className="bg-secondary/40 border-warmz-glass-border h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Personal">Pessoal</SelectItem>
                      <SelectItem value="Business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Features / Toggles */}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-5 space-y-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Funcionalidades</p>

              <ToggleRow
                icon={Globe}
                iconClass="text-muted-foreground"
                title="Rede Global"
                description="Em breve"
                checked={false}
                onCheckedChange={() => {}}
                disabled
              />

              <ToggleRow
                icon={ShieldCheck}
                title="Contatos Oficiais Verificados"
                description="Envia mensagens para números oficiais (bancos, operadoras) fortalecendo o histórico do chip."
                checked={officialContacts}
                onCheckedChange={setOfficialContacts}
              />

              <ToggleRow
                icon={Users}
                title="Grupos de Aquecimento"
                description="Entra em grupos de aquecimento verificados para simular atividade real e fortalecer o chip."
                checked={warmupGroups}
                onCheckedChange={setWarmupGroups}
              />

              {/* Import Contacts action */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                <div className="flex items-center gap-3">
                  <Download className="h-4 w-4 shrink-0 text-accent" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Importar Contatos do WhatsApp</p>
                    <p className="text-xs text-muted-foreground max-w-[280px]">
                      Importa os contatos salvos no WhatsApp para a sua lista.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  disabled={!isOnline || syncContacts.isPending}
                  onClick={() => syncContacts.mutate({ instance_id: instance.id })}
                >
                  {syncContacts.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Sincronizar
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right Column: Profile + Contacts */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="space-y-6">
          {/* Profile */}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-5 space-y-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Perfil de Aquecimento</p>
              <WarmupTimeline profiles={warmupProfiles} activeProfileId={profileId} />
            </CardContent>
          </Card>

          {/* Custom Contacts */}
          {hasContacts && (
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" /> Contatos para Aquecimento
                </p>
                <p className="text-xs text-muted-foreground">
                  Selecione tags ou contatos individuais para enviar mensagens de aquecimento.
                </p>

                {contactTags.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Tags</p>
                    <div className="max-h-40 overflow-y-auto space-y-0.5 pr-1">
                      {contactTags.map((tag) => (
                        <label key={tag.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/30 cursor-pointer">
                          <Checkbox
                            checked={selectedTags.includes(tag.id)}
                            onCheckedChange={(checked) => setSelectedTags((prev) => checked ? [...prev, tag.id] : prev.filter((x) => x !== tag.id))}
                          />
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tag.color || "#6366f1" }} />
                          <span className="text-sm text-foreground">{tag.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {customContacts.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Contatos Individuais</p>
                    <div className="max-h-48 overflow-y-auto space-y-0.5 pr-1">
                      {customContacts.map((c) => {
                        const isSelected = selectedContacts.includes(c.id);
                        const needsSave = isSelected && isOnline && !(instance as any).contacts_saved;
                        return (
                          <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/30 cursor-pointer">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => setSelectedContacts((prev) => checked ? [...prev, c.id] : prev.filter((x) => x !== c.id))}
                            />
                            <FlagImage iso2={guessCountryByPartialPhoneNumber({ phone: "+" + c.phone_number.replace(/\D/g, ""), countries: defaultCountries }).country?.iso2 || "br"} size="16px" />
                            <span className="text-sm text-foreground truncate">{c.name}</span>
                            {needsSave && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs max-w-[220px]">
                                  Salve este contato na agenda do WhatsApp dos outros chips para ativar o aquecimento.
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <span className="text-xs text-muted-foreground font-mono ml-auto shrink-0">{c.phone_number}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* User Groups Selection */}
          {userGroups.length > 0 && (
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-2">
                  <UsersRound className="h-3.5 w-3.5" /> Seus Grupos de Aquecimento
                </p>
                <p className="text-xs text-muted-foreground">
                  Selecione seus grupos personalizados para este número participar.
                </p>
                <div className="max-h-48 overflow-y-auto space-y-0.5 pr-1">
                  {userGroups.map((g) => (
                    <label key={g.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/30 cursor-pointer">
                      <Checkbox
                        checked={selectedGroups.includes(g.id)}
                        onCheckedChange={(checked) => setSelectedGroups((prev) => checked ? [...prev, g.id] : prev.filter((x) => x !== g.id))}
                      />
                      <Users className="h-3.5 w-3.5 text-accent shrink-0" />
                      <span className="text-sm text-foreground truncate">{g.name}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>

      {/* Save Button */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Button onClick={handleSave} disabled={saving || updateInstance.isPending} className="w-full glow-orange-sm h-12 text-base gap-2">
          {(saving || updateInstance.isPending) ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          Salvar Alterações
        </Button>
      </motion.div>

      {/* Confirm delete dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir número?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação é irreversível. O número será desconectado e removido permanentemente do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Sim, excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
